import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Mail, RefreshCw } from "lucide-react";
import {
  EmailClientPreview,
  EmailHtmlDraft,
  EmailMascotDraft,
  TemplateTile,
  buildEmailDraftPreview,
} from "./NotificationTemplateParts";
import {
  BuyEmailTemplateListItem,
  BuyEmailTemplatePreview,
  buyAdminTemplateApi,
} from "../../../services/buyAdminApi";
import BuyAdminThemeToggle from "../shared/BuyAdminThemeToggle";
import "../shared/buyAdminThemeGlobals.css";
import styles from "./NotificationTemplates.module.css";

const DEFAULT_EMAIL_TEMPLATE_FOLDER = "order-received";
const EMAIL_TEMPLATE_VIEW = "desktop" as const;

/** Renewal lifecycle templates — shown last until subscription renewal is live in app. */
const DEFERRED_RENEWAL_TEMPLATE_FOLDERS = new Set([
  "autorenew-membership",
  "autorenew-card-update",
  "renewal-confirmation",
  "membership-cancellation",
]);

const sortEmailTemplatesForDisplay = (items: BuyEmailTemplateListItem[]) =>
  [...items].sort((a, b) => {
    const aDeferred = DEFERRED_RENEWAL_TEMPLATE_FOLDERS.has(a.folder) ? 1 : 0;
    const bDeferred = DEFERRED_RENEWAL_TEMPLATE_FOLDERS.has(b.folder) ? 1 : 0;
    if (aDeferred !== bDeferred) return aDeferred - bDeferred;
    return 0;
  });

const AUTORENEW_TEMPLATE_FALLBACK: BuyEmailTemplateListItem = {
  folder: "autorenew-membership",
  metadata: null,
  mascotAssetKey: null,
  ready: true,
  lastModified: null,
};

const AUTORENEW_TEMPLATE_META = {
  templateName: "AutoRenew Membership",
  subject: "Your QuickAsyst membership renews on {{renewalDate}}",
};

const AUTORENEW_CARD_UPDATE_TEMPLATE: BuyEmailTemplateListItem = {
  folder: "autorenew-card-update",
  metadata: null,
  mascotAssetKey: null,
  ready: true,
  lastModified: null,
};

const AUTORENEW_CARD_UPDATE_META = {
  templateName: "Auto-Renew Card Update",
  subject: "Action Required: Update Your QuickAsyst Payment Info",
};

const RENEWAL_CONFIRMATION_TEMPLATE_FALLBACK: BuyEmailTemplateListItem = {
  folder: "renewal-confirmation",
  metadata: null,
  mascotAssetKey: null,
  ready: true,
  lastModified: null,
};

const RENEWAL_CONFIRMATION_TEMPLATE_META = {
  templateName: "Renewal Confirmation",
  subject: "You're renewed. See you out there.",
};

const MEMBERSHIP_CANCELLATION_TEMPLATE_FALLBACK: BuyEmailTemplateListItem = {
  folder: "membership-cancellation",
  metadata: null,
  mascotAssetKey: null,
  ready: true,
  lastModified: null,
};

const MEMBERSHIP_CANCELLATION_TEMPLATE_META = {
  templateName: "Membership Cancellation",
  subject: "Your QuickAsyst Membership Has Been Cancelled",
};

const CARD_DECLINED_TEMPLATE_FALLBACK: BuyEmailTemplateListItem = {
  folder: "card-declined",
  metadata: null,
  mascotAssetKey: null,
  ready: true,
  lastModified: null,
};

const CARD_DECLINED_TEMPLATE_META = {
  templateName: "Card Declined",
  subject: "Your QuickAsyst Membership Payment Didn't Go Through",
};

const AFFILIATE_TEMPLATE_FALLBACKS: BuyEmailTemplateListItem[] = [
  {
    folder: "affiliate-direct",
    metadata: {
      templateName: "Affiliate Direct",
      subject: "Direct Affiliate",
      variables: [],
      source: "local",
    },
    mascotAssetKey: "assets/affiliate-direct.png",
    ready: true,
    lastModified: null,
  },
  {
    folder: "affiliate-indirect",
    metadata: {
      templateName: "Affiliate Indirect",
      subject: "Indirect Affiliate",
      variables: [],
      source: "local",
    },
    mascotAssetKey: "assets/affiliate-indirect.png",
    ready: true,
    lastModified: null,
  },
];

const getTemplateByFolder = (emailTemplates: BuyEmailTemplateListItem[], folder: string) =>
  emailTemplates.find((item) => item.folder === folder) ||
  AFFILIATE_TEMPLATE_FALLBACKS.find((item) => item.folder === folder) ||
  (folder === AUTORENEW_TEMPLATE_FALLBACK.folder ? AUTORENEW_TEMPLATE_FALLBACK : null) ||
  (folder === AUTORENEW_CARD_UPDATE_TEMPLATE.folder ? AUTORENEW_CARD_UPDATE_TEMPLATE : null) ||
  (folder === RENEWAL_CONFIRMATION_TEMPLATE_FALLBACK.folder ? RENEWAL_CONFIRMATION_TEMPLATE_FALLBACK : null) ||
  (folder === MEMBERSHIP_CANCELLATION_TEMPLATE_FALLBACK.folder ? MEMBERSHIP_CANCELLATION_TEMPLATE_FALLBACK : null) ||
  (folder === CARD_DECLINED_TEMPLATE_FALLBACK.folder ? CARD_DECLINED_TEMPLATE_FALLBACK : null) ||
  null;

const getAutoRenewTemplate = (emailTemplates: BuyEmailTemplateListItem[]) =>
  getTemplateByFolder(emailTemplates, AUTORENEW_TEMPLATE_FALLBACK.folder) ||
  AUTORENEW_TEMPLATE_FALLBACK;

const getCardUpdateTemplate = (emailTemplates: BuyEmailTemplateListItem[]) =>
  getTemplateByFolder(emailTemplates, AUTORENEW_CARD_UPDATE_TEMPLATE.folder) ||
  AUTORENEW_CARD_UPDATE_TEMPLATE;

const getRenewalConfirmationTemplate = (emailTemplates: BuyEmailTemplateListItem[]) =>
  getTemplateByFolder(emailTemplates, RENEWAL_CONFIRMATION_TEMPLATE_FALLBACK.folder) ||
  RENEWAL_CONFIRMATION_TEMPLATE_FALLBACK;

const getMembershipCancellationTemplate = (emailTemplates: BuyEmailTemplateListItem[]) =>
  getTemplateByFolder(emailTemplates, MEMBERSHIP_CANCELLATION_TEMPLATE_FALLBACK.folder) ||
  MEMBERSHIP_CANCELLATION_TEMPLATE_FALLBACK;

const getCardDeclinedTemplate = (emailTemplates: BuyEmailTemplateListItem[]) =>
  getTemplateByFolder(emailTemplates, CARD_DECLINED_TEMPLATE_FALLBACK.folder) ||
  CARD_DECLINED_TEMPLATE_FALLBACK;

const formatDate = (value: string | null | undefined) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
};

const NotificationTemplates = () => {
  const [emailTemplates, setEmailTemplates] = useState<BuyEmailTemplateListItem[]>([]);
  const [usesLocalEmailTemplates, setUsesLocalEmailTemplates] = useState(false);
  const [localEmailTemplatesDir, setLocalEmailTemplatesDir] = useState<string | null>(null);
  const [selectedEmailFolder, setSelectedEmailFolder] = useState<string>(DEFAULT_EMAIL_TEMPLATE_FOLDER);
  const [emailPreview, setEmailPreview] = useState<BuyEmailTemplatePreview | null>(null);
  const [mascotPreviewUrl, setMascotPreviewUrl] = useState<string | null>(null);
  const [footerLogoPreviewUrl, setFooterLogoPreviewUrl] = useState<string | null>(null);
  const [draftHtml, setDraftHtml] = useState<EmailHtmlDraft | null>(null);
  const [draftMascot, setDraftMascot] = useState<EmailMascotDraft | null>(null);
  const [selectedHtmlFileName, setSelectedHtmlFileName] = useState("");
  const [selectedMascotFileName, setSelectedMascotFileName] = useState("");
  const [emailSubjectText, setEmailSubjectText] = useState("");
  const [savedEmailSubject, setSavedEmailSubject] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const selectedEmail = useMemo(
    () => getTemplateByFolder(emailTemplates, selectedEmailFolder),
    [emailTemplates, selectedEmailFolder],
  );

  const sortedEmailTemplates = useMemo(
    () => sortEmailTemplatesForDisplay(emailTemplates),
    [emailTemplates],
  );

  const hasSubjectDraft = emailSubjectText.trim() !== savedEmailSubject.trim();

  const emailDisplayPreview = useMemo(
    () =>
      buildEmailDraftPreview({
        draft: draftHtml,
        folder: selectedEmailFolder,
        view: EMAIL_TEMPLATE_VIEW,
        savedHtml: emailPreview?.html,
        htmlTemplate: emailPreview?.htmlTemplate,
        subjectTemplate: savedEmailSubject,
        draftSubject: hasSubjectDraft ? emailSubjectText : null,
        savedSubject: emailPreview?.subject,
        sampleVariables: emailPreview?.sampleVariables || {},
        mascotUrl: mascotPreviewUrl,
        mascotDraft: draftMascot,
        footerLogoUrl: footerLogoPreviewUrl,
      }),
    [
      draftHtml,
      selectedEmailFolder,
      emailPreview,
      savedEmailSubject,
      hasSubjectDraft,
      emailSubjectText,
      mascotPreviewUrl,
      draftMascot,
      footerLogoPreviewUrl,
    ],
  );

  const hasUnsavedEmailDraft =
    (draftHtml?.folder === selectedEmailFolder) ||
    draftMascot?.folder === selectedEmailFolder ||
    hasSubjectDraft;

  const displayMascotUrl =
    draftMascot?.folder === selectedEmailFolder
      ? draftMascot.previewUrl
      : mascotPreviewUrl;

  const loadLists = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const emailResponse = await buyAdminTemplateApi.getEmailTemplates();
      setEmailTemplates(emailResponse.data);
      setUsesLocalEmailTemplates(Boolean(emailResponse.meta?.usesLocalTemplates));
      setLocalEmailTemplatesDir(emailResponse.meta?.localTemplatesDir || null);
      setSelectedEmailFolder((current) => {
        if (current) return current;
        const preferred = emailResponse.data.find((item) => item.folder === DEFAULT_EMAIL_TEMPLATE_FOLDER);
        return preferred?.folder || emailResponse.data[0]?.folder || "";
      });
    } catch (error: any) {
      setErrorMessage(error?.message || "Failed to load templates.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadEmailDetail = useCallback(async (folder: string) => {
    if (!folder) return;
    setErrorMessage("");
    try {
      const [previewResponse, mascotBlob, footerLogoBlob] = await Promise.all([
        buyAdminTemplateApi.getEmailTemplatePreview(folder, EMAIL_TEMPLATE_VIEW),
        buyAdminTemplateApi.fetchEmailMascotBlob(folder).catch(() => null),
        buyAdminTemplateApi.fetchEmailFooterLogoBlob(folder).catch(() => null),
      ]);
      setEmailPreview(previewResponse.data);
      setMascotPreviewUrl((current) => {
        if (current) URL.revokeObjectURL(current);
        return mascotBlob ? URL.createObjectURL(mascotBlob) : null;
      });
      setFooterLogoPreviewUrl((current) => {
        if (current) URL.revokeObjectURL(current);
        return footerLogoBlob ? URL.createObjectURL(footerLogoBlob) : null;
      });
    } catch (error: any) {
      setEmailPreview(null);
      setErrorMessage(error?.message || "Failed to load email preview.");
    }
  }, []);

  useEffect(() => {
    loadLists();
  }, [loadLists]);

  useEffect(() => {
    return () => {
      if (footerLogoPreviewUrl) {
        URL.revokeObjectURL(footerLogoPreviewUrl);
      }
      if (mascotPreviewUrl) {
        URL.revokeObjectURL(mascotPreviewUrl);
      }
    };
  }, [footerLogoPreviewUrl, mascotPreviewUrl]);

  useEffect(() => {
    setDraftHtml(null);
    setDraftMascot((current) => {
      if (current?.previewUrl) URL.revokeObjectURL(current.previewUrl);
      return null;
    });
    setSelectedHtmlFileName("");
    setSelectedMascotFileName("");
    setSuccessMessage("");
  }, [selectedEmailFolder]);

  useEffect(() => {
    if (selectedEmailFolder) {
      loadEmailDetail(selectedEmailFolder);
    }
  }, [selectedEmailFolder, loadEmailDetail]);

  useEffect(() => {
    if (!selectedEmailFolder) return;
    const subject = selectedEmail?.metadata?.subject || "";
    setEmailSubjectText(subject);
    setSavedEmailSubject(subject);
  }, [selectedEmailFolder, selectedEmail?.metadata?.subject]);

  useEffect(
    () => () => {
      if (draftMascot?.previewUrl) {
        URL.revokeObjectURL(draftMascot.previewUrl);
      }
    },
    [draftMascot],
  );

  const handleEmailHtmlSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedEmailFolder) return;
    setSuccessMessage("");
    setErrorMessage("");
    try {
      const raw = await file.text();
      setDraftHtml({
        folder: selectedEmailFolder,
        view: EMAIL_TEMPLATE_VIEW,
        file,
        raw,
      });
      setSelectedHtmlFileName(file.name);
      setSuccessMessage("HTML loaded for preview. Review it, then click Save to publish to S3.");
    } catch (error: any) {
      setErrorMessage(error?.message || "Failed to read HTML file.");
    } finally {
      event.target.value = "";
    }
  };

  const handleMascotSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedEmailFolder) return;
    setSuccessMessage("");
    setErrorMessage("");
    if (draftMascot?.previewUrl) {
      URL.revokeObjectURL(draftMascot.previewUrl);
    }
    const previewUrl = URL.createObjectURL(file);
    setDraftMascot({
      folder: selectedEmailFolder,
      file,
      previewUrl,
    });
    setSelectedMascotFileName(file.name);
    setSuccessMessage("Mascot loaded for preview. Review it, then click Save to publish to S3.");
    event.target.value = "";
  };

  const handleDiscardEmailDraft = () => {
    if (draftMascot?.previewUrl) {
      URL.revokeObjectURL(draftMascot.previewUrl);
    }
    setDraftHtml(null);
    setDraftMascot(null);
    setSelectedHtmlFileName("");
    setSelectedMascotFileName("");
    setEmailSubjectText(savedEmailSubject);
    setSuccessMessage("");
    setErrorMessage("");
    if (selectedEmailFolder) {
      loadEmailDetail(selectedEmailFolder);
    }
  };

  const handleSaveEmailDraft = async () => {
    if (!selectedEmailFolder) return;
    const hasHtmlDraft = draftHtml?.folder === selectedEmailFolder;
    const hasMascotDraft = draftMascot?.folder === selectedEmailFolder;
    if (!hasHtmlDraft && !hasMascotDraft && !hasSubjectDraft) return;

    setSaving(true);
    setSuccessMessage("");
    setErrorMessage("");

    const savedParts: string[] = [];
    const failedParts: string[] = [];
    let htmlSaved = false;
    let mascotSaved = false;

    try {
      if (hasHtmlDraft && draftHtml) {
        try {
          const htmlBlob = new Blob([draftHtml.raw], { type: "text/html" });
          await buyAdminTemplateApi.uploadEmailTemplateHtml(
            selectedEmailFolder,
            EMAIL_TEMPLATE_VIEW,
            htmlBlob,
            selectedHtmlFileName || "desktop.html",
          );
          savedParts.push("HTML");
          htmlSaved = true;
        } catch (error: any) {
          failedParts.push(`HTML (${error?.message || "upload failed"})`);
        }
      }

      if (hasMascotDraft && draftMascot) {
        try {
          await buyAdminTemplateApi.uploadEmailTemplateMascot(selectedEmailFolder, draftMascot.file);
          savedParts.push("mascot");
          mascotSaved = true;
        } catch (error: any) {
          failedParts.push(`mascot (${error?.message || "upload failed"})`);
        }
      }

      if (hasSubjectDraft) {
        try {
          await buyAdminTemplateApi.saveEmailTemplateSubject(selectedEmailFolder, emailSubjectText.trim());
          savedParts.push("subject");
          setSavedEmailSubject(emailSubjectText.trim());
        } catch (error: any) {
          failedParts.push(`subject (${error?.message || "save failed"})`);
        }
      }

      if (failedParts.length > 0 && savedParts.length === 0) {
        throw new Error(`Failed to save: ${failedParts.join("; ")}`);
      }

      if (htmlSaved) {
        setDraftHtml(null);
        setSelectedHtmlFileName("");
      }
      if (mascotSaved) {
        if (draftMascot?.previewUrl) {
          URL.revokeObjectURL(draftMascot.previewUrl);
        }
        setDraftMascot(null);
        setSelectedMascotFileName("");
      }

      if (failedParts.length > 0) {
        setErrorMessage(`Partial save — saved ${savedParts.join(", ")}; failed: ${failedParts.join("; ")}`);
      } else {
        setSuccessMessage(`Saved to S3: ${savedParts.join(", ")}.`);
      }

      await loadLists();
      await loadEmailDetail(selectedEmailFolder);
    } catch (error: any) {
      setErrorMessage(error?.message || "Failed to save template to S3.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`${styles.page} buy-admin-theme-scope`}>
      <div className={styles.header}>
        <div>
          <h1>Email Templates</h1>
          <p>Choose a file to preview locally first. Nothing is sent to S3 until you click Save.</p>
        </div>
        <div className={`${styles.headerActions} buy-admin-theme-actions`}>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={loadLists}
            disabled={loading}
            aria-label="Refresh templates"
            title="Refresh"
          >
            <RefreshCw size={16} className={loading ? styles.spin : undefined} />
          </button>
          <BuyAdminThemeToggle iconOnly />
        </div>
      </div>

      <div className={styles.tabs}>
        <button type="button" className={styles.tabActive}>
          <Mail size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />
          Email
        </button>
      </div>

      {errorMessage ? <div className={styles.errorMessage}>{errorMessage}</div> : null}
      {successMessage ? <div className={styles.statusMessage}>{successMessage}</div> : null}

      <div className={styles.templatePicker}>
        <div className={styles.templatePickerLabel}>Email templates</div>
        <div className={styles.tileGrid}>
          {sortedEmailTemplates.map((item) => (
            <TemplateTile
              key={item.folder}
              title={item.metadata?.templateName || item.folder}
              subtitle={item.metadata?.subject || item.folder}
              meta={formatDate(item.lastModified)}
              ready={item.ready}
              selected={item.folder === selectedEmailFolder}
              onClick={() => setSelectedEmailFolder(item.folder)}
            />
          ))}
        </div>
      </div>

      <div className={styles.workspace}>
        {selectedEmail ? (
          <>
            <div className={styles.detailHeader}>
              <div>
                <h2>{selectedEmail.metadata?.templateName || selectedEmail.folder}</h2>
                <p className={styles.detailSubtext}>
                  One template for all devices — layout adapts on mobile via CSS. Production sends <strong>desktop.html</strong>.
                </p>
              </div>
            </div>

            <div className={styles.previewHero}>
              {usesLocalEmailTemplates && emailPreview?.templateSource === "local" ? (
                <div className={styles.draftBanner}>
                  Local dev — preview reads <strong>tmp_templates</strong>
                  {localEmailTemplatesDir ? ` (${localEmailTemplatesDir})` : ""}. After{" "}
                  <strong>Save to S3</strong>, files are mirrored to S3 and local so preview stays in sync.
                </div>
              ) : null}
              {hasUnsavedEmailDraft ? (
                <div className={styles.draftBanner}>
                  Unsaved preview — review your changes below, then click <strong>Save to S3</strong> to replace the live template.
                </div>
              ) : null}
              {emailDisplayPreview.html ? (
                <EmailClientPreview
                  html={emailDisplayPreview.html}
                  subject={emailDisplayPreview.subject}
                />
              ) : (
                <div className={styles.emptyState}>No preview available.</div>
              )}
            </div>

            <div className={styles.editorGrid}>
              <div className={styles.editorCard}>
                <div className={styles.formGroup}>
                  <label>Choose HTML file (desktop.html)</label>
                  <input
                    className={styles.fileInput}
                    type="file"
                    accept=".html,text/html"
                    onChange={handleEmailHtmlSelect}
                    disabled={saving}
                  />
                  {selectedHtmlFileName ? (
                    <div className={styles.selectedFileName}>Selected: {selectedHtmlFileName}</div>
                  ) : null}
                </div>
                <div className={styles.formGroup}>
                  <label>Choose mascot image (PNG)</label>
                  <input
                    className={styles.fileInput}
                    type="file"
                    accept="image/png"
                    onChange={handleMascotSelect}
                    disabled={saving || !selectedEmail.mascotAssetKey}
                  />
                  {selectedMascotFileName ? (
                    <div className={styles.selectedFileName}>Selected: {selectedMascotFileName}</div>
                  ) : null}
                  {displayMascotUrl ? (
                    <img src={displayMascotUrl} alt="Mascot preview" className={styles.mascotPreview} />
                  ) : null}
                </div>
                <div className={styles.editorActions}>
                  <button
                    type="button"
                    className={styles.primaryBtn}
                    onClick={handleSaveEmailDraft}
                    disabled={saving || !hasUnsavedEmailDraft}
                  >
                    {saving ? "Saving..." : "Save to S3"}
                  </button>
                  <button
                    type="button"
                    className={styles.secondaryBtn}
                    onClick={handleDiscardEmailDraft}
                    disabled={saving || !hasUnsavedEmailDraft}
                  >
                    Discard changes
                  </button>
                </div>
              </div>
              <div className={styles.editorCard}>
                <div className={styles.formGroup}>
                  <label>Variables</label>
                  {selectedEmail.metadata?.variables?.includes("inHandExpectedAvailabilityBlock") && (
                    <div className={styles.fieldHint}>
                      Ticket availability appears whenever an in-hand date is available, including TM Mobile and e-ticket orders.
                    </div>
                  )}
                  <div className={styles.variables}>
                    {(selectedEmail.metadata?.variables || []).map((variable) => (
                      <span key={variable} className={styles.variableChip}>
                        {`{{${variable}}}`}
                      </span>
                    ))}
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <label>Email subject</label>
                  <input
                    className={styles.subjectInput}
                    type="text"
                    value={emailSubjectText}
                    onChange={(event) => setEmailSubjectText(event.target.value)}
                    placeholder="Your QuickAsyst verification code"
                  />
                  <div className={styles.fieldHint}>
                    Stored in S3 as <code>metadata.json</code>. You can use variables like {"{{userName}}"}.
                  </div>
                  {emailDisplayPreview.subject ? (
                    <div className={styles.subjectPreview}>
                      Inbox preview: {emailDisplayPreview.subject}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className={styles.emptyState}>Select an email template.</div>
        )}
      </div>
    </div>
  );
};

export default NotificationTemplates;
